import { useState, useEffect } from 'react';
import { Download, TrendingUp, TrendingDown, Calendar, DollarSign, Scale } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface BSLineItem {
  id: string;
  name: string;
  type: 'header' | 'subheader' | 'line_item' | 'subtotal' | 'calculated';
  category?: string;
  level: number;
  value?: number;
  priorValue?: number;
  change?: number;
  changePercent?: number;
  highlight?: boolean;
  bold?: boolean;
  children?: BSLineItem[];
}

interface BalanceSheet {
  id: string;
  organizationId: string;
  templateId?: string;
  periodEnd: string;
  format: string;
  scenario: string;
  data: {
    lineItems: BSLineItem[];
  };
  totals: {
    totalAssets: number;
    totalCurrentAssets: number;
    netFixedAssets: number;
    totalLiabilities: number;
    totalCurrentLiabilities: number;
    totalLongTermLiabilities: number;
    totalEquity: number;
    totalLiabilitiesEquity: number;
    workingCapital: number;
    currentRatio: number;
    debtToEquityRatio: number;
  };
  priorPeriodData?: {
    periodEnd: string;
    lineItems: BSLineItem[];
  };
  createdAt: string;
  updatedAt: string;
}

interface BalanceSheetStatementProps {
  balanceSheetId?: string;
  onGenerate?: () => void;
}

const BalanceSheetStatement = ({ balanceSheetId, onGenerate }: BalanceSheetStatementProps) => {
  const [balanceSheet, setBalanceSheet] = useState<BalanceSheet | null>(null);
  const [loading, setLoading] = useState(false);
  const [showComparative, setShowComparative] = useState(false);

  useEffect(() => {
    if (balanceSheetId) {
      loadBalanceSheet();
    }
  }, [balanceSheetId]);

  useEffect(() => {
    if (balanceSheet?.priorPeriodData) {
      setShowComparative(true);
    }
  }, [balanceSheet]);

  const loadBalanceSheet = async () => {
    if (!balanceSheetId) return;

    setLoading(true);
    try {
      const response = await api.get(`/balance-sheet/statements/${balanceSheetId}`);
      setBalanceSheet(response.data.data);
    } catch (error: any) {
      toast.error('Failed to load balance sheet');
      console.error('Error loading balance sheet:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number | undefined): string => {
    if (value === undefined || value === null) return '$0.00';

    const formatted = Math.abs(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return value < 0 ? `($${formatted})` : `$${formatted}`;
  };

  const formatPercent = (value: number | undefined): string => {
    if (value === undefined || value === null) return '0.0%';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const formatRatio = (value: number | undefined): string => {
    if (value === undefined || value === null) return '0.00';
    return value.toFixed(2);
  };

  const renderLineItem = (item: BSLineItem, index: number) => {
    const isHeader = item.type === 'header';
    const isSubheader = item.type === 'subheader';
    const isSubtotal = item.type === 'subtotal' || item.type === 'calculated';
    const indentClass = `pl-${Math.min(item.level * 6, 24)}`;

    const hasComparative = showComparative && item.priorValue !== undefined;

    return (
      <div key={item.id || index}>
        <div
          className={`grid ${
            hasComparative ? 'grid-cols-4' : 'grid-cols-2'
          } py-2 border-b border-neutral-100 hover:bg-neutral-50 ${
            item.highlight ? 'bg-primary-50' : ''
          } ${isSubtotal ? 'border-t-2 border-neutral-300 font-semibold' : ''} ${
            isHeader ? 'bg-neutral-100 font-bold' : ''
          }`}
        >
          <div
            className={`${indentClass} ${
              item.bold || isSubtotal || isHeader ? 'font-bold' : ''
            } ${
              isHeader
                ? 'font-bold text-neutral-900 uppercase text-sm'
                : isSubheader
                ? 'font-semibold text-neutral-700 italic'
                : 'text-neutral-900'
            }`}
          >
            {item.name}
          </div>

          <div
            className={`text-right ${item.bold || isSubtotal ? 'font-bold' : ''} ${
              isHeader || isSubheader ? '' : 'text-neutral-900'
            }`}
          >
            {!isHeader && !isSubheader && formatCurrency(item.value)}
          </div>

          {hasComparative && (
            <>
              <div className="text-right text-neutral-600">
                {!isHeader && !isSubheader && formatCurrency(item.priorValue)}
              </div>
              <div
                className={`text-right ${
                  item.change && item.change > 0 ? 'text-success-600' : item.change && item.change < 0 ? 'text-alert-600' : 'text-neutral-900'
                }`}
              >
                {!isHeader && !isSubheader && item.change !== undefined && (
                  <div className="flex items-center justify-end space-x-1">
                    {item.change > 0 ? (
                      <TrendingUp className="w-3 h-3" />
                    ) : item.change < 0 ? (
                      <TrendingDown className="w-3 h-3" />
                    ) : null}
                    <span>{formatPercent(item.changePercent)}</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {item.children && item.children.map((child, idx) => renderLineItem(child, idx))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!balanceSheet) {
    return (
      <div className="card text-center py-12">
        <Scale className="w-16 h-16 mx-auto mb-4 text-neutral-300" />
        <h3 className="text-lg font-semibold text-neutral-700 mb-2">No Balance Sheet</h3>
        <p className="text-neutral-600 mb-4">
          Generate a balance sheet to get started
        </p>
        {onGenerate && (
          <button onClick={onGenerate} className="btn btn-primary">
            Generate Balance Sheet
          </button>
        )}
      </div>
    );
  }

  const hasComparative = showComparative && balanceSheet.priorPeriodData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-neutral-900">
              Balance Sheet
              <span className="ml-3 text-sm font-normal text-neutral-600 capitalize">
                ({balanceSheet.format})
              </span>
            </h2>
            <div className="flex items-center space-x-4 mt-2 text-sm text-neutral-600">
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>As of {new Date(balanceSheet.periodEnd).toLocaleDateString()}</span>
              </div>
              <span>•</span>
              <span className="capitalize bg-primary-100 text-primary-800 px-2 py-1 rounded">
                {balanceSheet.scenario}
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {balanceSheet.priorPeriodData && (
              <button
                onClick={() => setShowComparative(!showComparative)}
                className="btn btn-secondary text-sm"
              >
                {showComparative ? 'Hide' : 'Show'} Comparative
              </button>
            )}
            <button className="btn btn-secondary">
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
          </div>
        </div>

        {/* Key Metrics Summary */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-primary-50 rounded-lg border border-primary-200">
            <p className="text-sm text-primary-700 mb-1">Total Assets</p>
            <p className="text-2xl font-bold text-primary-900">
              {formatCurrency(balanceSheet.totals.totalAssets)}
            </p>
          </div>

          <div className="p-4 bg-alert-50 rounded-lg border border-alert-200">
            <p className="text-sm text-alert-700 mb-1">Total Liabilities</p>
            <p className="text-2xl font-bold text-alert-900">
              {formatCurrency(balanceSheet.totals.totalLiabilities)}
            </p>
          </div>

          <div className="p-4 bg-success-50 rounded-lg border border-success-200">
            <p className="text-sm text-success-700 mb-1">Total Equity</p>
            <p className="text-2xl font-bold text-success-900">
              {formatCurrency(balanceSheet.totals.totalEquity)}
            </p>
          </div>
        </div>

        {/* Financial Ratios */}
        <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-neutral-50 rounded-lg">
          <div>
            <p className="text-sm text-neutral-600 mb-1">Working Capital</p>
            <p className="text-lg font-semibold text-neutral-900">
              {formatCurrency(balanceSheet.totals.workingCapital)}
            </p>
          </div>
          <div>
            <p className="text-sm text-neutral-600 mb-1">Current Ratio</p>
            <p className="text-lg font-semibold text-neutral-900">
              {formatRatio(balanceSheet.totals.currentRatio)}:1
            </p>
          </div>
          <div>
            <p className="text-sm text-neutral-600 mb-1">Debt-to-Equity Ratio</p>
            <p className="text-lg font-semibold text-neutral-900">
              {formatRatio(balanceSheet.totals.debtToEquityRatio)}:1
            </p>
          </div>
        </div>

        {/* Balance Sheet Equation */}
        <div className="p-4 bg-neutral-900 rounded-lg text-white">
          <div className="flex items-center justify-center space-x-4 text-lg">
            <div>
              <span className="text-neutral-400">Assets: </span>
              <span className="font-bold">{formatCurrency(balanceSheet.totals.totalAssets)}</span>
            </div>
            <span className="text-neutral-500">=</span>
            <div>
              <span className="text-neutral-400">Liabilities: </span>
              <span className="font-bold">
                {formatCurrency(balanceSheet.totals.totalLiabilities)}
              </span>
            </div>
            <span className="text-neutral-500">+</span>
            <div>
              <span className="text-neutral-400">Equity: </span>
              <span className="font-bold">{formatCurrency(balanceSheet.totals.totalEquity)}</span>
            </div>
          </div>
          {Math.abs(balanceSheet.totals.totalAssets - balanceSheet.totals.totalLiabilitiesEquity) > 1 && (
            <p className="text-center text-alert-400 text-sm mt-2">
              ⚠ Balance sheet equation is not balanced
            </p>
          )}
        </div>
      </div>

      {/* Detailed Statement */}
      <div className="card">
        <div
          className={`grid ${
            hasComparative ? 'grid-cols-4' : 'grid-cols-2'
          } py-3 border-b-2 border-neutral-300 font-semibold text-sm text-neutral-700 bg-neutral-50`}
        >
          <div>Account</div>
          <div className="text-right">
            {hasComparative ? new Date(balanceSheet.periodEnd).toLocaleDateString() : 'Amount'}
          </div>
          {hasComparative && (
            <>
              <div className="text-right">
                {new Date(balanceSheet.priorPeriodData!.periodEnd).toLocaleDateString()}
              </div>
              <div className="text-right">Change</div>
            </>
          )}
        </div>

        <div className="divide-y divide-neutral-100">
          {balanceSheet.data.lineItems.map((item, index) => renderLineItem(item, index))}
        </div>
      </div>
    </div>
  );
};

export default BalanceSheetStatement;
